import axios from 'axios'
import {
  Api,
  ContentType,
  type FullRequestParams,
  type HttpResponse,
  type QueryParamsType
} from './Api'
import { StorageKeys } from '@/utils/constants/storage.keys'
import Config from '@/utils/config'

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(StorageKeys.ACCESS_TOKEN)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  const { API_URL } = Config
  config.baseURL = API_URL
  config.headers['Target-URL'] = API_URL

  return config
})

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error || {}

    const pathname = window.location.pathname
    if (
      response?.status === 401 &&
      pathname !== '/' &&
      pathname !== '/verify'
    ) {
      localStorage.removeItem(StorageKeys.ACCESS_TOKEN)
      window.location.href = '/'
    }

    return Promise.reject(error)
  }
)

type SecurityData = { token?: string | null }

export class CustomApi extends Api<SecurityData> {
  public request = async <T = unknown, E = unknown>({
    body,
    method,
    path,
    type,
    query,
    baseUrl,
    cancelToken,
    ...params
  }: FullRequestParams): Promise<HttpResponse<T, E>> => {
    const requestParams = this.mergeRequestParams(params)
    const queryString = query && this.toQueryString(query)
    const payloadFormatter = this.formatter[type || ContentType.Json];

    const requestHeaders: Record<string, string> = {};
    if (requestParams.headers instanceof Headers) {
      requestParams.headers.forEach((value, key) => {
        requestHeaders[key] = value;
      });
    } else {
      Object.entries(requestParams.headers || {}).forEach(([key, value]) => {
        requestHeaders[key] = value;
      });
    }

    return axios.request<T, HttpResponse<T, E>>({
      url: `${baseUrl || this.baseUrl || ''}${path}${queryString ? `?${queryString}` : ''}`,
      method,
      headers: {
        ...requestHeaders,
        ...(type && type !== ContentType.FormData
          ? { 'Content-Type': type }
          : {})
      } as Record<string, string>,
      data: typeof body === 'undefined' || body === null ? null : payloadFormatter(body),
      signal: cancelToken ? this.createAbortSignal(cancelToken) : undefined
    })
  }

  formatter: Record<ContentType, (input: unknown) => unknown> = {
    [ContentType.Json]: (input: unknown) =>
      input !== null && (typeof input === 'object' || typeof input === 'string')
        ? JSON.stringify(input)
        : input,
    [ContentType.JsonApi]: (input: unknown) =>
      input !== null && (typeof input === 'object' || typeof input === 'string')
        ? JSON.stringify(input)
        : input,
    [ContentType.Text]: (input: unknown) =>
      input !== null && typeof input !== 'string'
        ? JSON.stringify(input)
        : input,
    [ContentType.FormData]: (input: unknown) => {
      if (input instanceof FormData) {
        return input
      }

      return Object.keys(input || {}).reduce((formData, key) => {
        const property = (input as Record<string, unknown>)[key]
        formData.append(
          key,
          property instanceof Blob
            ? property
            : typeof property === 'object' && property !== null
              ? JSON.stringify(property)
              : `${property}`
        )
        return formData
      }, new FormData())
    },
    [ContentType.UrlEncoded]: (input: unknown) => this.toQueryString(input as QueryParamsType)
  }
}
